import { useEffect } from 'react';
import { useParams, useLocation } from 'wouter';
import { setSlugCompradorAtual } from '../../lib/compradorTrpc';
import { sessaoCompradorAtual, limparSessaoComprador } from '../../lib/compradorAuth';

/**
 * Prefixo ÚNICO da URL pública do app do comprador. Fonte única — toda rota,
 * link e navegação do app deriva daqui, então mudar o prefixo é uma linha só.
 * `/comprador` continua atendido como REDIRECT legado (ver App.tsx) para os
 * links de e-mail já enviados. NÃO confundir com a pasta `pages/comprador/`
 * (código), com `/api/comprador/*` (download de boleto) nem com
 * `/trpc-comprador` (endpoint tRPC) — esses são internos e não mudam.
 */
export const APP_BASE = '/app';
export const rotaApp = (slug: string, sub = ''): string => `${APP_BASE}/${slug}${sub}`;

/** R$ 148.500 (sem centavos — números do design são redondos e mais calmos). */
export const brl = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MESES_LONGOS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/** '2026-02-10' → '10 fev 2026'. */
export function dataCurta(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return `${d} ${MESES[m - 1]} ${y}`;
}

/** '2026-12-01' → 'Dezembro 2026'. */
export function mesLongo(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const [y, m] = iso.split('-').map(Number);
  if (!y || !m) return null;
  return `${MESES_LONGOS[m - 1]} ${y}`;
}

/** '2026-05-01' → 'mai 2025' (curto, sem dia). */
export function mesCurto(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const [y, m] = iso.split('-').map(Number);
  if (!y || !m) return null;
  return `${MESES[m - 1]} ${y}`;
}

/**
 * Hook de toda tela logada do comprador: registra o slug no client tRPC e
 * redireciona pro login se não houver sessão salva. Devolve o slug.
 */
export function useCompradorSessao(): string | undefined {
  const { slug } = useParams<{ slug: string }>();
  const [, navigate] = useLocation();
  useEffect(() => {
    setSlugCompradorAtual(slug ?? null);
    if (slug && !sessaoCompradorAtual(slug)) navigate(rotaApp(slug), { replace: true });
  }, [slug, navigate]);
  return slug;
}

/** Sessão expirada/revogada no meio do uso: limpa e volta pro login. */
export function sairParaLogin(slug: string, navigate: (p: string, o?: { replace?: boolean }) => void): void {
  limparSessaoComprador(slug);
  navigate(rotaApp(slug), { replace: true });
}
